import { computed, type Ref, watch } from 'vue'
import { useTranslationsStore } from '@/vue/stores/translationsStore'

/**
 * A Vue composable that dynamically sets the page title based on translations and optional entity data.
 *
 * This composable watches for changes in the translation key and entity reference, then updates
 * the page title accordingly using the setPageTitle utility.
 *
 * @param {string} page - Translation key for the page title (e.g., 'pages.dashboard', 'pages.portfolio')
 * @param {Ref<string | null | undefined>} [entity] - Optional reactive reference to an entity name (e.g., asset name, portfolio name)
 * @returns {void}
 *
 * @example
 * // Basic usage with just a translation key
 * usePageTitle('pages.dashboard')
 *
 * @example
 * // With an entity name
 * const portfolioName = ref('My Portfolio')
 * usePageTitle('pages.portfolio', portfolioName)
 */
export function usePageTitle(page: string, entity?: Ref<string | null | undefined>) {
    const { translate } = useTranslationsStore()
    const title = computed(() => translate(page))

    watch(
        [
            () => title.value,
            () => entity?.value,
        ],
        ([title, entity]) => {
            if (title) {
                const args = [entity, title].filter(Boolean) as string[]
                setPageTitle(...args)
            }
        },
        { immediate: true },
    )
}

const DEFAULT_TITLE = 'Scaler'

/**
 * Sets the document title by joining the provided arguments with the default title
 *
 * @param {...string[]} args - One or more strings to be included in the page title
 * @example
 * // Sets title to "Home - Scaler"
 * setPageTitle('Home')
 *
 * @example
 * // Sets title to "Dashboard - Admin - Scaler"
 * setPageTitle('Dashboard', 'Admin')
 *
 * @example
 * // Sets title to just "Scaler"
 * setPageTitle()
 */
export function setPageTitle(...args: string[]) {
    if (args.length > 0) {
        document.title = [...args, DEFAULT_TITLE].join(' - ')
    } else {
        document.title = DEFAULT_TITLE
    }
}
